<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Locales, LocalizableString } from '@/services/types/api.types';
import { getTranslation } from '@/composables/data-display-utils';
import { i18n } from '@/plugins/i18n';

// PROPS
const props = defineProps<{
  selectedForm: any | null;
  languageEnabled: Locales[];
}>();

// EMITS
const emit = defineEmits<{
  'value-selected': [
    valueData: { answerId: number; valueId: number | string; valueText: LocalizableString; stepType: string } | null,
  ];
}>();

// COMPOSABLES
const { t } = useI18n();

function getOtherChoiceFallbackLabel(): LocalizableString {
  const enMessages = i18n.global.getLocaleMessage('en') as Record<string, any>;
  const frMessages = i18n.global.getLocaleMessage('fr') as Record<string, any>;

  return {
    en: enMessages.form.filter['other-choice'],
    fr: frMessages.form.filter['other-choice'],
  };
}

// REACTIVE STATE
const selectedValue = ref<{
  answerId: number;
  valueId: number | string;
  valueText: LocalizableString;
  stepType: string;
} | null>(null);

// COMPUTED
const hasChoices = computed(() => {
  if (!props.selectedForm?.answers?.steps) return false;
  return props.selectedForm.answers.steps.some((step: { type: string }) => step.type === 'choices');
});

const valuesList = computed(() => {
  if (!props.selectedForm?.answers?.steps) {
    return [];
  }

  const allValues: Array<{
    answerId: number;
    valueId: number | string;
    valueText: LocalizableString;
    stepType: string;
  }> = [];

  for (const step of props.selectedForm.answers.steps as Array<{
    answerId: number;
    type: string;
    otherChoice?: boolean;
    otherChoiceLabel?: LocalizableString | null;
    choices?: Array<{ id: number; text: LocalizableString }>;
  }>) {
    if (step.type === 'choices' && step.choices) {
      for (const choice of step.choices) {
        allValues.push({
          answerId: step.answerId,
          valueId: choice.id,
          valueText: choice.text,
          stepType: 'choices',
        });
      }

      if (step.otherChoice) {
        allValues.push({
          answerId: step.answerId,
          valueId: 'o',
          valueText: step.otherChoiceLabel ?? getOtherChoiceFallbackLabel(),
          stepType: 'choices',
        });
      }
    }
  }

  return allValues;
});

function optionLabel(option: { valueText: LocalizableString }) {
  return getTranslation(option, 'valueText', props.languageEnabled);
}

// WATCHERS
watch(selectedValue, (newValue) => {
  emit('value-selected', newValue || null);
});

watch(
  () => props.selectedForm,
  () => {
    selectedValue.value = null;
  },
);
</script>

<template>
  <div class="form-value-selector">
    <q-select
      v-if="selectedForm && hasChoices"
      v-model="selectedValue"
      :options="valuesList"
      :option-label="optionLabel"
      :label="t('form.filter.select-value')"
      outlined
      clearable
      :placeholder="t('form.filter.value-placeholder')"
    />
    <q-banner v-else-if="selectedForm && !hasChoices" class="q-mt-md bg-warning text-black" dense>
      {{ t('form.filter.unsupported-form-type') }}
    </q-banner>
    <div v-else class="text-grey-6">
      {{ t('form.filter.select-form-first') }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.form-value-selector {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
